import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVisibleNav } from './Sidebar';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import {
  LayoutGrid, Plus, Settings2, Check, ClipboardList, Route, UserPlus, AlarmClock, Play, IndianRupee,
} from 'lucide-react';

// Quick-create actions the user can pick from (filtered to what they can access)
const QUICK_ACTIONS = [
  { key: 'task', to: '/task-tracker', label: 'New Task', icon: ClipboardList },
  { key: 'travel', to: '/travel-plans', label: 'Start Travel Plan', icon: Route },
  { key: 'unplanned', to: '/unplanned-visits', label: 'Start Visit', icon: Play },
  { key: 'lead', to: '/leads', label: 'New Lead', icon: UserPlus },
  { key: 'reminder', to: '/reminders', label: 'New Reminder', icon: AlarmClock },
];

const SLOTS_KEY = (uid) => `mobile_bar_slots_${uid || 'anon'}`;
const readSlots = (uid) => { try { return JSON.parse(localStorage.getItem(SLOTS_KEY(uid)) || 'null'); } catch { return null; } };

export const MobileTabBar = ({ uid, onProfile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const groups = useVisibleNav();
  const [sheet, setSheet] = useState(false);
  const [customize, setCustomize] = useState(false);

  // all accessible routes → {to,label,icon} (first occurrence wins per route)
  const allItems = useMemo(() => {
    const m = {};
    groups.forEach((g) => g.items.forEach((it) => { if (!m[it.to]) m[it.to] = it; }));
    return m;
  }, [groups]);
  const can = (to) => !!allItems[to];

  // right-slot shortcuts: user config → else default [Revenue, Tasks] (accessible only)
  const defaultSlots = useMemo(() => {
    const d = [];
    if (can('/revenue')) d.push('/revenue');
    if (can('/task-tracker')) d.push('/task-tracker');
    // pad from any accessible items if fewer than 2
    groups.forEach((g) => g.items.forEach((it) => { if (d.length < 2 && !d.includes(it.to)) d.push(it.to); }));
    return d.slice(0, 2);
  }, [groups]); // eslint-disable-line

  const [slots, setSlots] = useState(() => {
    const saved = readSlots(uid);
    return (saved && saved.length ? saved : null);
  });
  const effectiveSlots = (slots && slots.filter(can)) ;
  const rightSlots = (effectiveSlots && effectiveSlots.length ? effectiveSlots : defaultSlots).slice(0, 2);

  const quickActions = QUICK_ACTIONS.filter((a) => can(a.to));

  const go = (to) => { setSheet(false); navigate(to); };
  const toggleSlot = (to) => {
    setSlots((prev) => {
      const base = (prev && prev.length ? prev : defaultSlots).filter(can);
      let next;
      if (base.includes(to)) next = base.filter((x) => x !== to);
      else next = [...base, to].slice(-2);  // keep last two picked
      try { localStorage.setItem(SLOTS_KEY(uid), JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };

  const onHome = location.pathname === '/home';
  const Item = ({ active, onClick, icon: Icon, label, testid, raised }) => (
    <button type="button" data-testid={testid} onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center min-h-[58px] ${raised ? '-mt-5' : 'py-1.5'}`}>
      {raised ? (
        <div className="w-12 h-12 rounded-2xl bg-amber-600 text-white flex items-center justify-center shadow-lg shadow-amber-600/40 active:scale-95 transition-transform">
          <Icon className="w-6 h-6" strokeWidth={2.2} />
        </div>
      ) : (
        <>
          <div className={`flex items-center justify-center w-11 h-8 rounded-xl ${active ? 'bg-amber-100 text-amber-700' : 'text-slate-500'}`}>
            <Icon className="w-5 h-5" strokeWidth={active ? 2.2 : 1.7} />
          </div>
          <span className={`text-[10px] font-bold leading-none mt-0.5 ${active ? 'text-amber-700' : 'text-slate-500'}`}>{label}</span>
        </>
      )}
      {raised && <span className="text-[10px] font-bold text-amber-700 leading-none mt-0.5">{label}</span>}
    </button>
  );

  const slotLabel = (to) => (allItems[to]?.label || to);
  const slotIcon = (to) => (allItems[to]?.icon || IndianRupee);

  return (
    <>
      <nav data-testid="mobile-tab-bar"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-2px_12px_rgba(0,0,0,0.06)] flex items-stretch justify-around"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <Item testid="tabbar-home" active={onHome} onClick={() => navigate('/home')} icon={LayoutGrid} label="Home" />
        {rightSlots[0] && <Item testid="tabbar-slot-0" active={location.pathname === rightSlots[0]} onClick={() => navigate(rightSlots[0])} icon={slotIcon(rightSlots[0])} label={slotLabel(rightSlots[0])} />}
        <Item testid="tabbar-plus" raised onClick={() => setSheet(true)} icon={Plus} label="Add" />
        {rightSlots[1] && <Item testid="tabbar-slot-1" active={location.pathname === rightSlots[1]} onClick={() => navigate(rightSlots[1])} icon={slotIcon(rightSlots[1])} label={slotLabel(rightSlots[1])} />}
        {!rightSlots[1] && <div className="flex-1" />}
      </nav>

      {/* Quick create + customize sheet */}
      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent side="bottom" className="rounded-t-2xl p-0 max-h-[80vh] overflow-y-auto" data-testid="tabbar-sheet">
          <SheetHeader className="p-4 border-b border-slate-100">
            <SheetTitle className="text-base flex items-center gap-2"><Plus className="w-4 h-4 text-amber-600" /> Quick create</SheetTitle>
          </SheetHeader>
          <div className="p-3 grid grid-cols-2 gap-2.5">
            {quickActions.length === 0 && <div className="col-span-2 text-center text-sm text-slate-400 py-6">No quick actions available</div>}
            {quickActions.map((a) => (
              <button key={a.key} data-testid={`tabbar-quick-${a.key}`} onClick={() => go(a.to)}
                className="flex items-center gap-2.5 bg-slate-50 border border-slate-100 rounded-xl px-3 py-3 active:scale-95 transition-transform">
                <span className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><a.icon className="w-4.5 h-4.5" /></span>
                <span className="text-sm font-semibold text-slate-700 text-left leading-tight">{a.label}</span>
              </button>
            ))}
          </div>
          <div className="px-3 pb-3">
            <button data-testid="tabbar-customize-toggle" onClick={() => setCustomize((c) => !c)}
              className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 border border-slate-200 rounded-xl py-2.5">
              <Settings2 className="w-3.5 h-3.5" /> Customize bottom-bar shortcuts
            </button>
            {customize && (
              <div className="mt-2 border border-slate-100 rounded-xl p-2" data-testid="tabbar-customize">
                <div className="text-[11px] text-slate-400 px-1 pb-1.5">Pick up to 2 shortcuts for the bar (e.g. Revenue, Task Tracker).</div>
                <div className="max-h-56 overflow-y-auto grid grid-cols-1 gap-1">
                  {Object.values(allItems).map((it) => {
                    const picked = rightSlots.includes(it.to);
                    const Icon = it.icon || IndianRupee;
                    return (
                      <button key={it.to} data-testid={`tabbar-opt-${it.to.replace(/\//g, '')}`} onClick={() => toggleSlot(it.to)}
                        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm ${picked ? 'bg-amber-50 text-amber-800' : 'text-slate-600'}`}>
                        <Icon className="w-4 h-4" />
                        <span className="flex-1 text-left truncate">{it.label}</span>
                        {picked && <Check className="w-4 h-4 text-amber-600" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
